import { Initiative, PLOT_STATUS } from '@schema/initiative'
import { PhaseType } from '@schema/phase'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '../blocks/workflow'
import { CreationGuide } from '../blocks/CreationGuide'
import { useEffect, useState } from 'react'
import { useInitiativeAutoSave } from '../blocks/useInitiativeAutoSave'
import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { YesNoToggle } from '@app/components/core/toggle/YesNoToggle'
import { Text } from '@app/components/core/typography/Text'
import { Style } from 'inlines'
import { TextInput } from '@app/components/core/input/text/TextInput'
import { YesNoOtherToggle } from '@app/components/core/toggle/YesNoOtherToggle'

export const LegalInfoStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
  uncompleteStep,
}: {
  initiative: Partial<Initiative>
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}) => {
  const translations = useTranslations().app
  const creationTranslations = translations.dashboard.initiative.creation
  const [potentialPlotStatus, setPotentialPlotStatus] = useState(
    initiative.potentialPlotStatus ?? PLOT_STATUS.noPlot,
  )
  const [potentialPlotAddress, setPotentialPlotAddress] = useState(
    initiative.potentialPlotAddress ?? '',
  )

  const [permit, setPermit] = useState(initiative.permit)
  const [
    financingRequirementsConstruction,
    setFinancingRequirementsConstruction,
  ] = useState(initiative.financingRequirementsConstruction)
  const [financingRequirementsPlanning, setFinancingRequirementsPlanning] =
    useState(initiative.financingRequirementsPlanning)
  const [kvkRegistration, setKvkRegistration] = useState(
    initiative.kvkRegistration,
  )

  useInitiativeAutoSave(initiative.id, {
    potentialPlotStatus,
    potentialPlotAddress,
    permit,
    financingRequirementsConstruction,
    financingRequirementsPlanning,
    kvkRegistration,
  })

  const renderPlotQuestions = potentialPlotStatus !== PLOT_STATUS.noPlot

  useEffect(() => {
    const stepValid =
      (renderPlotQuestions && potentialPlotAddress.trim().length > 5) ||
      !renderPlotQuestions
    if (stepValid) {
      completeStep(initiativeCreationSteps.legalInfo)
    } else {
      uncompleteStep(initiativeCreationSteps.legalInfo)
    }
  }, [renderPlotQuestions, potentialPlotAddress, completeStep, uncompleteStep])

  return (
    <CreationGuide
      selectedPhaseType={selectedPhaseType}
      currentStep={initiativeCreationSteps.legalInfo}
    >
      <Column gap="2.5rem" alignItems="stretch">
        <Column gap="0.5rem">
          <Text.Medium isBold={true}>
            {creationTranslations.plotUnderOption}
          </Text.Medium>
          <YesNoOtherToggle
            optionValues={{
              yes: PLOT_STATUS.underOption,
              no: PLOT_STATUS.noPlot,
              other: PLOT_STATUS.potentialPlotInMind,
            }}
            otherLabel={creationTranslations.notUnderOptionButInMind}
            selected={potentialPlotStatus}
            setSelected={setPotentialPlotStatus}
          />
        </Column>
        {renderPlotQuestions && (
          <TextInput
            label={creationTranslations.address}
            required={true}
            labelStyle={labelStyle}
            text={potentialPlotAddress}
            setText={setPotentialPlotAddress}
          />
        )}
        {renderPlotQuestions && (
          <Column gap="0.5rem">
            <Text.Medium isBold={true}>
              {creationTranslations.permit}
            </Text.Medium>
            <YesNoToggle yesSelected={permit} setYesSelected={setPermit} />
          </Column>
        )}
        <Column gap="0.5rem">
          <Text.Medium isBold={true}>
            {creationTranslations.financingRequirementsConstruction}
          </Text.Medium>
          <YesNoToggle
            yesSelected={financingRequirementsConstruction}
            setYesSelected={setFinancingRequirementsConstruction}
          />
        </Column>
        <Column gap="0.5rem">
          <Text.Medium isBold={true}>
            {creationTranslations.financingRequirementsPlanning}
          </Text.Medium>
          <YesNoToggle
            yesSelected={financingRequirementsPlanning}
            setYesSelected={setFinancingRequirementsPlanning}
          />
        </Column>
        <Column gap="0.5rem">
          <Text.Medium isBold={true}>
            {creationTranslations.kvkRegistration}
          </Text.Medium>
          <YesNoToggle
            yesSelected={kvkRegistration}
            setYesSelected={setKvkRegistration}
          />
        </Column>
      </Column>
    </CreationGuide>
  )
}

const labelStyle: Style = {
  color: 'var(--brand-secondary)',
  fontWeight: 500,
}
